import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Autocomplete, FormControl, TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePickerInput, Modal } from '../../../../../../shared/components';
import {
  TrialBillingStatus, TrialStatus, TrialType,
} from '../../../../../../shared/enums';
import companiesService from '../../../../../companies/services/companies-service';
import Company from '../../../../../../models/company';
import TrialsFilterAttributes from '../../../../../../interfaces/trials/trials-filter-attributes';
import getTrialStatusAttributes from '../../../../../../shared/utils/trial/get-trial-status-attributes';
import formHelper from '../../../../../../shared/utils/form-helper';
import getTrialBillingStatusAttributes from '../../../../../../shared/utils/trial/get-trial-billing-status-attributes';
import Task from '../../../../../../models/task';
import tasksService from '../../../../../tasks/service/tasks-service';

interface Props {
  onTrialsFilterModalChange: (trialsFilterAttributes: TrialsFilterAttributes) => void;
  isOpen: boolean;
  onClose: () => void;
}

export default function TrialsFilterModal(
  {
    onTrialsFilterModalChange, isOpen, onClose,
  }: Props,
) {
  const [isCompaniesFetching, setIsCompaniesFetching] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isTasksFetching, setIsTasksFetching] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskFilter, setTaskFilter] = useState<Task | null>(null);
  const [statusFilter, setStatusFilter] = useState<TrialStatus | null>(null);
  const [clientFilter, setClientFilter] = useState<Company | null>(null);
  const [officeFilter, setOfficeFilter] = useState<Company | null>(null);
  const [billingStatusFilter, setBillingStatusFilter] = useState<TrialBillingStatus | null>(null);
  const [trialTypeFilter, setTrialTypeFilter] = useState<TrialType | null>(null);

  const fetchCompaniesData = async () => {
    setIsCompaniesFetching(true);
    const fetchedCompanies = await companiesService.getCompanies().catch(() => []);
    setCompanies(fetchedCompanies);
    setIsCompaniesFetching(false);
  };

  const fetchTasksData = async () => {
    setIsTasksFetching(true);
    const fetchedTasks = await tasksService.getTasks().catch(() => []);
    setTasks(fetchedTasks);
    setIsTasksFetching(false);
  };

  const {
    handleSubmit, control, setValue,
  } = useForm<TrialsFilterAttributes>({ mode: 'onTouched' });

  const handleOnClose = () => {
    onClose();
  };

  const handleTrialsFilterChange = (trialsFilterAttributes: TrialsFilterAttributes) => {
    onTrialsFilterModalChange(formHelper.setEmptyStringsToUndefined<TrialsFilterAttributes>(trialsFilterAttributes));
    handleOnClose();
  };

  useEffect(() => {
    fetchCompaniesData();
    fetchTasksData();
  }, []);

  return (
    <Modal title="Filtragem de Processos" isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(handleTrialsFilterChange)} className="flex flex-col">
        <div className="w-full flex flex-row justify-between mb-5">
          <div className="w-[48%]">
            <FormControl variant="standard" fullWidth>
              <Autocomplete
                id="task-select"
                options={tasks}
                disabled={isTasksFetching}
                getOptionLabel={(option: Task) => option.name}
                value={taskFilter}
                onChange={(_, newValue) => {
                  setValue('taskId', formHelper.getEmptyStringFromUndefined(newValue?.taskId));
                  setTaskFilter(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="client-select"
                    label="Tipo de Processo"
                    variant="standard"
                    fullWidth
                  />
                )}
              />
            </FormControl>
          </div>

          <div className="w-[48%]">
            <FormControl variant="standard" fullWidth>
              <Autocomplete
                id="status-select"
                options={Object.values(TrialStatus)}
                getOptionLabel={(option: TrialStatus) => getTrialStatusAttributes(option).text}
                value={statusFilter}
                onChange={(_, newValue) => {
                  setValue('status', formHelper.getEmptyStringFromUndefined(newValue));
                  setStatusFilter(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="status-select"
                    label="Status"
                    variant="standard"
                    fullWidth
                  />
                )}
              />
            </FormControl>
          </div>
        </div>

        <div className="w-full flex flex-row justify-between mb-5">
          <div className="w-[48%]">
            <FormControl variant="standard" fullWidth>
              <Autocomplete
                id="client-select"
                options={companies}
                disabled={isCompaniesFetching}
                getOptionLabel={(option: Company) => option.name}
                value={clientFilter}
                onChange={(_, newValue) => {
                  setValue('clientId', formHelper.getEmptyStringFromUndefined(newValue?.companyId));
                  setClientFilter(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="client-select"
                    label="Cliente"
                    variant="standard"
                    fullWidth
                  />
                )}
              />
            </FormControl>
          </div>

          <div className="w-[48%]">
            <FormControl variant="standard" fullWidth>
              <Autocomplete
                id="office-select"
                options={companies}
                disabled={isCompaniesFetching}
                getOptionLabel={(option: Company) => option.name}
                value={officeFilter}
                onChange={(_, newValue) => {
                  setValue('officeId', formHelper.getEmptyStringFromUndefined(newValue?.companyId));
                  setOfficeFilter(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="office-select"
                    label="Escritório"
                    variant="standard"
                    fullWidth
                  />
                )}
              />
            </FormControl>
          </div>
        </div>

        <div className="w-full flex flex-row justify-between mb-5">
          <div className="w-[48%]">
            <Controller
              control={control}
              name="fromCreatedAt"
              render={({
                field: {
                  onChange,
                  value,
                },
              }) => (
                <DatePickerInput onChange={onChange} value={value} label="De data de criação" />
              )}
            />
          </div>

          <div className="w-[48%]">
            <Controller
              control={control}
              name="toCreatedAt"
              render={({
                field: {
                  onChange,
                  value,
                },
              }) => (
                <DatePickerInput onChange={onChange} value={value} label="Até data de criação" />
              )}
            />
          </div>
        </div>

        <div className="w-full flex flex-row justify-between mb-5">
          <div className="w-[48%]">
            <Controller
              control={control}
              name="fromDeadline"
              render={({
                field: {
                  onChange,
                  value,
                },
              }) => (
                <DatePickerInput onChange={onChange} value={value} label="De prazo" />
              )}
            />
          </div>

          <div className="w-[48%]">
            <Controller
              control={control}
              name="toDeadline"
              render={({
                field: {
                  onChange,
                  value,
                },
              }) => (
                <DatePickerInput onChange={onChange} value={value} label="Até prazo" />
              )}
            />
          </div>
        </div>

        <div className="w-full flex flex-row justify-between mb-5">
          <div className="w-[48%]">

            <FormControl variant="standard" fullWidth>
              <Autocomplete
                id="status-select"
                options={Object.values(TrialType).slice(0, 3)} // Display only the first 3 elements
                getOptionLabel={(option: TrialType) => option}
                value={trialTypeFilter}
                onChange={(_, newValue) => {
                  setValue('trialType', formHelper.getEmptyStringFromUndefined(newValue));
                  setTrialTypeFilter(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="type-select"
                    label="Tipo do processo"
                    variant="standard"
                    fullWidth
                  />
                )}
              />

            </FormControl>
          </div>

          <div className="w-[48%]">

            <FormControl variant="standard" fullWidth>
              <Autocomplete
                id="status-select"
                options={Object.values(TrialBillingStatus)}
                getOptionLabel={(option: TrialBillingStatus) => getTrialBillingStatusAttributes(option).text}
                value={billingStatusFilter}
                onChange={(_, newValue) => {
                  setValue('billingStatus', formHelper.getEmptyStringFromUndefined(newValue));
                  setBillingStatusFilter(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="status-select"
                    label="Status do Pagamento"
                    variant="standard"
                    fullWidth
                  />
                )}
              />
            </FormControl>
          </div>
        </div>

        <div className="mt-7">
          <LoadingButton
            variant="contained"
            type="submit"
          >
            Aplicar filtro
          </LoadingButton>
        </div>
      </form>
    </Modal>
  );
}
